import type { AddressStyle, PreferenceValues } from '../contract/types';

/**
 * Postal addresses, laid out per country convention.
 *
 * Layout follows the *destination*, not the reader: a Mexican depot is addressed the Mexican way
 * whoever is looking at it, because the layout is a property of where the envelope is going. The
 * reader's resolved style answers only when the address names no country this module can place.
 *
 * The templates use the `{city}, {state} {zip}` grammar `profile-api` writes its address patterns
 * in — `line1Pattern`, `cityLinePattern` and the rest — so the day `ResolvedFormats` carries the
 * backend's own patterns the renderer reads them unchanged. Only style codes arrive today, so the
 * layouts live here.
 */

export type AddressFields = {
  readonly line1?: string;
  readonly line2?: string;
  readonly city?: string;
  readonly state?: string;
  readonly zip?: string;
  readonly country?: string;
};

type AddressLayout = {
  /** One template per line, in order. A line whose fields are all absent is dropped. */
  readonly lines: readonly string[];
  /** How the lines join when they have to fit on one. */
  readonly separator: string;
};

const LAYOUTS: Readonly<Record<AddressStyle, AddressLayout>> = {
  US_STANDARD: {
    lines: ['{line1}', '{line2}', '{city}, {state} {zip}', '{country}'],
    separator: ', ',
  },
  /**
   * Mexico puts the postal code before the city and the state after it, and the neighbourhood —
   * the colonia — on its own line above. `line2` carries it, which is where the backend's
   * `line2Pattern` puts it too.
   */
  MX_STANDARD: {
    lines: ['{line1}', '{line2}', '{zip} {city}, {state}', '{country}'],
    separator: ', ',
  },
};

/**
 * The spellings of a destination this module has a layout for.
 *
 * `country` carries whatever the source system put in it: an ISO code from one endpoint, a display
 * name from another, in the reader's language or the destination's. Matching normalised spellings
 * is the only option available — `Intl.DisplayNames` turns a code into a name and offers no way
 * back — so this holds the forms that actually arrive rather than pretending to cover the world.
 *
 * Written as style-to-spellings and typed as a total record, so a style added to LAYOUTS
 * fails to compile here until a destination spelling reaches it. Lookups invert this table at
 * load. An unrecognised country is not an error: this module has no layout to prefer, and the
 * reader's own style is a better answer than a wrong one.
 */
const DESTINATIONS_BY_STYLE: Readonly<Record<AddressStyle, readonly string[]>> =
  {
    US_STANDARD: [
      'US',
      'USA',
      'UNITED STATES',
      'UNITED STATES OF AMERICA',
      'ESTADOS UNIDOS',
      'ETATS-UNIS',
      'ETATS UNIS',
      'CA',
      'CAN',
      'CANADA',
    ],
    MX_STANDARD: ['MX', 'MEX', 'MEXICO', 'MEXIQUE'],
  };

const STYLE_BY_DESTINATION: Readonly<Record<string, AddressStyle>> =
  Object.fromEntries(
    (Object.keys(DESTINATIONS_BY_STYLE) as AddressStyle[]).flatMap((style) =>
      DESTINATIONS_BY_STYLE[style].map((name) => [name, style])
    )
  );

/** Diacritics dropped, periods and commas dropped, whitespace collapsed: `U.S.A.` and `México` both land. */
const normaliseCountry = (country: string): string =>
  country
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[.,]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();

const layoutFor = (
  preferences: PreferenceValues,
  fields: AddressFields
): AddressLayout => {
  const destination = fields.country?.trim()
    ? STYLE_BY_DESTINATION[normaliseCountry(fields.country)]
    : undefined;

  return LAYOUTS[destination ?? preferences.formats.address];
};

const PLACEHOLDER = /\{(\w+)\}/g;

/**
 * Fills a template, dropping the punctuation that belonged to a field that is not there.
 *
 * Naive substitution is what produces `Detroit,  48201` for an address with no state, and that
 * stray comma is the tell of a system rendering a form rather than an address. So each literal is
 * treated as belonging to the field that follows it: no field, no separator.
 */
const fill = (template: string, fields: AddressFields): string => {
  const values = fields as Readonly<Record<string, string | undefined>>;
  let rendered = '';
  let cursor = 0;

  for (const match of template.matchAll(PLACEHOLDER)) {
    const index = match.index ?? 0;
    const literal = template.slice(cursor, index);
    const value = values[match[1] ?? '']?.trim();

    if (value) {
      // A separator only earns its place between two things, so the first field shown drops the
      // punctuation that would have preceded it.
      rendered += (rendered ? literal : literal.replace(/^[\s,]+/, '')) + value;
    }

    cursor = index + match[0].length;
  }

  return `${rendered}${template.slice(cursor)}`
    .replace(/\s+/g, ' ')
    .replace(/^[,\s]+|[,\s]+$/g, '');
};

/**
 * The address as the lines an envelope would carry.
 *
 * @param preferences - Supplies the style for an address whose country is absent or unrecognised.
 * @param fields - The address. Every field is optional; absent ones collapse.
 * @returns The non-empty lines, in order. An address with nothing in it gives an empty array.
 */
export const formatAddressLines = (
  preferences: PreferenceValues,
  fields: AddressFields
): string[] =>
  layoutFor(preferences, fields)
    .lines.map((template) => fill(template, fields))
    .filter((line) => line !== '');

/**
 * The address on one line.
 *
 * @param preferences - Supplies the style for an address whose country is absent or unrecognised.
 * @param fields - The address. Every field is optional; absent ones collapse.
 * @returns The lines joined, such as `1 Main St, Detroit, MI 48201`.
 */
export const formatAddress = (
  preferences: PreferenceValues,
  fields: AddressFields
): string =>
  formatAddressLines(preferences, fields).join(
    layoutFor(preferences, fields).separator
  );
